<template>
  <b-card
    no-body
    v-draggable
    ref="amc_survey_subcard"
    id="amc-survey-subcard"
  >
    <!-- Header -->
    <b-card-header
      style="color: lightgrey; height: 25px; cursor: move"
      class="pl-2 pb-0 pt-0"
      header-tag="div"
      header-bg-variant="dark"
    >
      <b-row class="mx-0 px-0">
        <b-col class="mx-0 px-0" cols="11">
          {{ $t('Webportal.CX.Dashboard.ActionManagement') }} - {{ $t('Webportal.Survey') }}
        </b-col>
        <b-col class="mx-0 px-0" cols="1" style="text-align: right">
          <font-awesome-icon
            class="mx-1 clickable"
            :class="{ disabled: !canNavigateBackward }"
            :icon="['far', 'backward']"
            @click="navigateBackward"
          />

          <font-awesome-icon
            class="mx-1 clickable"
            :class="{ disabled: !canNavigateForward }"
            :icon="['far', 'forward']"
            @click="navigateForward"
          />

          <font-awesome-icon
            class="mx-1 clickable"
            :icon="['far', 'times']"
            @click="$emit('close')"
          />
        </b-col>
      </b-row>
    </b-card-header>

    <!-- Content -->
    <b-card-text id="cardText" class="pl-3 pr-3 h-100 w-100" :class="{ spinner: showLoadingSpinner }" style="overflow: auto">
      <!-- PAGE.Intro -->
      <template v-if="currentPage == PAGE.Intro">
        <div class="intro-box" v-html="introText" />
      </template>

      <!-- PAGE.Result_Failure -->
      <template v-else-if="currentPage == PAGE.Result_Failure">
        <div class="intro-box">
          {{ $t("Webportal.CX.Dashboard.ActionManagementCenter.Survey.Failure") }}
          <br><br>
          <a @click="currentPage = PAGE.Info_PDF" class="clickable">
            <b>{{ $t("Webportal.CX.Dashboard.ActionManagementCenter.Survey.BackToInfo") }}</b>
          </a>
        </div>
      </template>

      <!-- PAGE.Result_Success -->
      <template v-else-if="currentPage == PAGE.Result_Success">
        <div class="intro-box">
          {{ $t("Webportal.CX.Dashboard.ActionManagementCenter.Survey.Success") }}
          <br><br>
          <a @click="$emit('close', actionToTake)" class="clickable">
            <b>{{ $t("Webportal.CX.Dashboard.ActionManagementCenter.Survey.CompleteAction") }}</b>
          </a>
        </div>
      </template>

      <!-- PAGE.Info_PDF -->
      <template v-else-if="currentPage == PAGE.Info_PDF">
        <iframe class="pdf-container" :src="firstAidPdf" frameBorder="0"></iframe>
      </template>

      <!-- PAGE.Survey / Fallback -->
      <form v-else @submit.prevent="submitQuestionary">
        <div class="question-group" v-if="hasValidQuestions">
          <h3 class="question-group-title">
            {{this.title}}
          </h3>

          <!-- <span class="question-group-description">
            {{this.description}}
          </span> -->

          <div class="question" v-for="(question, qIndex) in this.randomizedQuestions" :key="qIndex">
            <h4 class="question-title">
              {{qIndex + 1}}. {{question.Text}}
            </h4>

            <div v-for="(answer) in question.Answers" :key="answer.Id">
              <input type="radio" :name="`radio_${qIndex}`" :id="answer.Id" :questionId="question.Id" required>
              <label class="question-label" :for="answer.Id" >
                {{answer.Text}}
              </label>
            </div>
          </div>
        </div>

        <button id="submit">
          {{ $t("Webportal.CX.Dashboard.ActionManagementCenter.Submit") }}
        </button>
      </form>

      <div class="nav-buttons">
        <span>
          <font-awesome-icon
            class="mx-1 clickable"
            :class="{ disabled: !canNavigateBackward }"
            :icon="['far', 'backward']"
            @click="navigateBackward"
          />

          <font-awesome-icon
            class="mx-1 clickable"
            :class="{ disabled: !canNavigateForward }"
            :icon="['far', 'forward']"
            @click="navigateForward"
          />
        </span>
      </div>
    </b-card-text>
    
  </b-card>
</template>

<script>
const PAGE = Object.freeze({
  // Navigateable
  Intro: 0,
  Info_PDF: 1,
  Survey: 2,

  // Non navigatable
  Result_Failure: 10,
  Result_Success: 11,

  // Helper
  NAVIGATABLE_FIRST: 0,
  NAVIGATABLE_LAST: 2
});

import DashboardService from "@/services/DashboardService";
import CancelToken from '@/mixins/CancelToken';
import { i18n } from "@/i18n";

export default {
  name: "ActionManagementCenter_Survey",
  props: {
    actionToTake: Object,
    firstAidKit: Object,
    decimalThreshold: {
      type: Number,
      default: 0.7
    }
  },
  mixins: [ CancelToken ],

  computed: {
    hasValidQuestions: function() {
      return this.randomizedQuestions?.length > 0 && this.randomizedQuestions[0];
    },
    canNavigateForward: function() {
      return this.currentPage >= PAGE.NAVIGATABLE_FIRST && this.currentPage < PAGE.NAVIGATABLE_LAST;
    },
    canNavigateBackward: function() {
      return this.currentPage > PAGE.NAVIGATABLE_FIRST && this.currentPage <= PAGE.NAVIGATABLE_LAST;
    },
    showLoadingSpinner: function() {
      // Show spinner only on last page (survey), as we load the survey async
      return this.isLoading && !this.canNavigateForward;
    },
    firstAidPdf: function() {
      return this.firstAidKit?.FirstAidKit ?
        `${process.env.BASE_URL}api/administration/first-aid/group/${this.firstAidKit.FirstAidKit}?localization-id=${i18n.locale}` :
        null;
    },
    introText: function() {
      return `
        ${ this.$t("Webportal.CX.Dashboard.ActionManagementCenter.Survey.Intro") }
        <br><br>
        <b>
          <i>${
            this.$t("Webportal.CX.Dashboard.ActionManagementCenter.Survey.Instruction",
              { fa_forward: '&nbsp; <i class="fas fa-forward"></i>' }
            )
          }</i>
        </b>`;
    }
  },

  data() {
    return {
      PAGE: PAGE,
      currentPage: PAGE.NAVIGATABLE_FIRST,

      isLoading: false,
      title: '',
      randomizedQuestions: [],
      cancelToken: null
    };
  },

  created() {
    this.getSurvey();
  },

  methods: {
    async getSurvey() {
      this.isLoading = true;
      this.cancelToken = this.initCancelToken(this.cancelToken);

      return DashboardService.getSurveyJson(this.firstAidKit.AmcSurvey, i18n.locale, this.cancelToken)
        .then((response)=> {
          if (!response?.data) {
            console.error(`Loading of survey '${this.firstAidKit?.AmcSurvey}' failed!`);
            return;
          }

          this.title = _.first(response.data)?.SurveyTitle;
          
          // We do only use the first survey yet
          this.randomizedQuestions = _(response.data)
            .flatMap(survey => survey?.Sections)
            .flatMap(section => section?.Questions)
            .shuffle()
            .take(10)
            .value();

          if (!this.hasValidQuestions)
            console.error(`No Questions found for survey '${this.firstAidKit?.AmcSurvey}'.`);
        })
        .catch((error) => console.error("Failed getting survey from server", error))
        .finally(() => { this.isLoading = false; });
    },

    async submitQuestionary(e) {
      document.getElementById("cardText").scrollTop = 0;
      
      const pointsReached = _.sumBy(e.target, el => {
        // Check only checked!
        if (!el.checked) return;

        const questionId = el.attributes?.questionId?.value;
        const question = this.randomizedQuestions.find(q => q.Id == questionId);
        if (!question)
          return console.warn('Failed to find question');

        const answer = question.Answers.find(a => a.Id == el.id);
        if (!answer)
          return console.warn('Failed to find answer');

        return answer.Points;
      });

      // Failed
      const maxPoints = _.sumBy(this.randomizedQuestions, 'MaxPoints');
      const pointsThreshold = maxPoints * this.decimalThreshold;
      if (pointsReached < pointsThreshold) {
        console.info("Failed:\nThreshold: "+ pointsThreshold + "\nReached: " + pointsReached);
        this.currentPage = PAGE.Result_Failure;
        return;
      }

      // Success
      this.currentPage = PAGE.Result_Success;
    },

    navigateForward() {
      if (this.canNavigateForward)
        this.currentPage++;
    },
    navigateBackward() {
      if (this.canNavigateBackward)
        this.currentPage--;
    }
  },
};
</script>

<style scoped>
.disabled {
  cursor: not-allowed !important;
  opacity: 0.5 !important;
}
#survey-subcard {
  position: fixed !important;
  width: 50%;
}
#submit {
  margin: 20px;
  margin-bottom: 55px;
  padding: 10px;
  border-style: none;
  background: rgb(0, 115, 182);
  color: white;
  width: 150px;
}
.question-group {
  border: 2px solid rgb(173, 173, 173);
  margin-top: 5px;
}
.question-group-title {
  padding-left: 10px;
  font-size: 1.5rem;
  font-weight: 600;
}
.question-group-description {
  padding-left :10px;
  font-size: 1.2rem;
  font-weight: 400;
}
.question {
  border: 1px solid rgb(173, 173, 173);
  margin: 10px;
  padding-top: 10px;
  padding-bottom: 20px;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 1.0rem;
}
.question-label {
  margin-left: 5px;
  margin-bottom: 2px;
}
.question-title {
  margin-top: 0px;
  margin-bottom: 10px;
  margin-left: 5px;
  font-size: 1.0rem;
  font-weight: 600;
}
.answer {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.pdf-container {
  height: 95%;
  width: 100%;
}
.intro-box {
  position: relative;
  top: calc(50% - 125px);
  left: calc(50% - 125px);
  height: 250px;
  width: 250px;
  padding: 25px;
  border: solid black 1px;
}
.nav-buttons {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;

  color: white;
  background-color: black;
  font-size: 25px;
}
.nav-buttons span {
  width: 66px;
  margin-left: calc(50% - 33px);
}
</style>
